import type { FC, ReactNode } from 'react'
import type { ClaudeSession } from '@/features/events/conversation/state/types'
import type {
  TreeNodeStyleConfig,
  ContentContainerConfig,
  SessionIconRenderer,
} from '../types/SessionTreeProps'
import { ProjectNode } from '../ProjectNode'
import { SessionNodeRenderer } from './SessionNodeRenderer'

export interface ProjectGroup {
  readonly projectKey: string
  readonly projectName: string
  readonly sessions: ClaudeSession[]
}

export interface ProjectNodeRendererProps {
  readonly project: ProjectGroup
  readonly projectIcon: ReactNode | null | undefined
  readonly projectStyleConfig: TreeNodeStyleConfig
  readonly sessionIconConfig: ReactNode | SessionIconRenderer | null | undefined
  readonly sessionStyleConfig: TreeNodeStyleConfig
  readonly contentContainerConfig: ContentContainerConfig
  readonly expandPosition: 'left' | 'right'
  readonly showCounts: boolean
  readonly isExpanded: (key: string) => boolean
  readonly onToggle: (key: string) => void
  readonly projectActions?: ReactNode[] | undefined
  readonly projectStats?: ReactNode | undefined
  readonly getSessionActions?: ((session: ClaudeSession) => ReactNode[] | undefined) | undefined
  readonly getSessionPersistentActions?:
    | ((session: ClaudeSession) => ReactNode[] | undefined)
    | undefined
  readonly renderOrchestrator: (session: ClaudeSession, isExpanded: boolean) => ReactNode
}

export const ProjectNodeRenderer: FC<ProjectNodeRendererProps> = ({
  project,
  projectIcon,
  projectStyleConfig,
  sessionIconConfig,
  sessionStyleConfig,
  contentContainerConfig,
  expandPosition,
  showCounts,
  isExpanded,
  onToggle,
  projectActions,
  projectStats,
  getSessionActions,
  getSessionPersistentActions,
  renderOrchestrator,
}) => {
  return (
    <ProjectNode
      projectKey={project.projectKey}
      label={project.projectName}
      icon={projectIcon}
      styleConfig={projectStyleConfig}
      {...(showCounts && { count: project.sessions.length })}
      {...(projectActions && { actions: projectActions })}
      {...(projectStats && { stats: projectStats })}
      isExpanded={isExpanded(`project:${project.projectKey}`)}
      onToggle={() => onToggle(`project:${project.projectKey}`)}
      expandPosition={expandPosition}
    >
      {project.sessions.map((session) => (
        <SessionNodeRenderer
          key={session.sessionId}
          session={session}
          iconConfig={sessionIconConfig}
          styleConfig={sessionStyleConfig}
          contentContainerConfig={contentContainerConfig}
          expandPosition={expandPosition}
          isExpanded={isExpanded(`session:${session.sessionId}`)}
          onToggle={() => onToggle(`session:${session.sessionId}`)}
          actions={getSessionActions?.(session)}
          persistentActions={getSessionPersistentActions?.(session)}
          renderOrchestrator={renderOrchestrator}
        />
      ))}
    </ProjectNode>
  )
}
